import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingDown, Plus } from 'lucide-react';
import CadutaForm from './CadutaForm';

const LIV = { rosso: 'bg-red-100 text-red-700', giallo: 'bg-amber-100 text-amber-700', verde: 'bg-emerald-100 text-emerald-700' };

export default function CaduteCard({ ospiteId, onSaved }) {
  const { hasRole } = useAuth();
  const [dati, setDati] = useState(null);
  const [form, setForm] = useState(false);

  const carica = useCallback(() => {
    api.get(`/cadute/ospite/${ospiteId}`).then(r => setDati(r.data)).catch(() => {});
  }, [ospiteId]);

  useEffect(() => { carica(); }, [carica]);

  if (!dati) return null;

  return (
    <Card data-testid="cadute-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
          <TrendingDown className="w-4 h-4 text-brand" />Registro cadute
          <Badge variant="outline" className="text-[10px]" data-testid="cadute-totale">{dati.totale} totali</Badge>
          {dati.ultimi_30_giorni > 0 && <Badge className="text-[10px] bg-red-100 text-red-700">{dati.ultimi_30_giorni} negli ultimi 30 gg</Badge>}
          {dati.conley_da_rivalutare && <Badge className="text-[10px] bg-amber-100 text-amber-700" data-testid="badge-conley-da-rivalutare">Conley da rivalutare</Badge>}
          {hasRole('coordinatore', 'medico', 'infermiere', 'oss') && (
            <Button size="sm" className="h-7 text-xs gap-1 ml-auto bg-brand hover:bg-brand-dark text-white"
              onClick={() => setForm(true)} data-testid="registra-caduta-btn"><Plus className="w-3 h-3" />Registra caduta</Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {dati.cadute.length === 0 && <p className="text-xs text-emerald-700">Nessuna caduta registrata per questo ospite.</p>}
        {dati.cadute.slice(0, 5).map(c => (
          <div key={c.id} className="border rounded-lg p-3" data-testid={`caduta-voce-${c.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-slate-800">{new Date(c.data_ora).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</span>
              {c.turno && <Badge variant="outline" className="text-[9px] capitalize">{c.turno}</Badge>}
              {c.luogo && <Badge variant="outline" className="text-[9px]">{c.luogo}</Badge>}
              <Badge className={`text-[9px] ${LIV[c.livello] || 'bg-slate-100 text-slate-600'}`}>{c.gravita_label || c.conseguenze || 'Conseguenze non indicate'}</Badge>
            </div>
            {c.attivita && <p className="text-[11px] text-slate-500 mt-1">Attivita: {c.attivita}{c.testimoniata ? ' · testimoniata' : ''}{c.contenzione_presente ? ' · contenzione in atto' : ''}</p>}
            <p className="text-xs text-slate-600 mt-1">{c.dinamica}</p>
            {c.esami?.length > 0 && <p className="text-[11px] text-slate-500 mt-1">Esami: {c.esami.join(', ')}</p>}
            {(c.provvedimenti || c.interventi) && <p className="text-[11px] text-slate-500 mt-1">Provvedimenti: {c.provvedimenti || c.interventi}</p>}
          </div>
        ))}
      </CardContent>
      <CadutaForm open={form} onOpenChange={setForm} ospiteId={ospiteId} onSaved={() => { carica(); onSaved?.(); }} />
    </Card>
  );
}
